// src/admin/plans/plansModel.js — pure rules of the view-only «Piani» page (/admin/plans, v223).
//
// Data: GET /api/v1/admin/plans (unordered array; price / allowance_credits are decimal STRINGS) and
// GET /api/v1/admin/plan-features (one row per grant key = COALESCE(plan_family, id), alphabetical, each naming
// its plan_ids). The grid is what gives a plan its FAMILY — the plan list carries none.
//
// ⛔ Ordering is DERIVED from the data (a family sits where its cheapest plan sits, null price last), never a
//    hard-coded family list: a new family needs no code change.
// ⛔ i18n keys for features are built from the option's `value` (or the fixed `pipeline`), NEVER from `feature_id`:
//    `cg:out:blog-seo` contains ':', the i18next namespace separator.
// ⛔ Pure: inputs are never mutated.
import { parseDecimal } from '../../utils/creditsFormat.js';

export const PIPELINE_FEATURE_ID = 'cg:pipeline';
const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/** { planId: grantKey } from the grid's rows. `{}` when there is no grid. */
export function familyByPlan(grid) {
  const out = {};
  for (const row of Array.isArray(grid?.grants) ? grid.grants : []) {
    for (const id of Array.isArray(row?.plan_ids) ? row.plan_ids : []) out[id] = row.grant_key;
  }
  return out;
}

// Price ascending; a null (or unparsable) price sorts last; ties by id for a stable order.
function byPrice(a, b) {
  const pa = parseDecimal(a?.price);
  const pb = parseDecimal(b?.price);
  if (pa == null && pb != null) return 1;
  if (pb == null && pa != null) return -1;
  if (pa != null && pb != null && pa !== pb) return pa - pb;
  return String(a?.id).localeCompare(String(b?.id));
}

/**
 * [{ family, plans[] }]. With a grid: one group per family, families in the order of their cheapest plan.
 * Without one: a single group `{ family: null }` holding every plan by price.
 */
export function groupPlans(plans, grid) {
  const sorted = Array.isArray(plans) ? [...plans].sort(byPrice) : [];
  if (sorted.length === 0) return [];
  const families = familyByPlan(grid);
  if (Object.keys(families).length === 0) return [{ family: null, plans: sorted }];
  const groups = new Map();
  for (const p of sorted) {
    const key = families[p.id] ?? p.id; // the backend's own COALESCE(plan_family, id)
    groups.set(key, [...(groups.get(key) ?? []), p]);
  }
  return [...groups.entries()].map(([family, list]) => ({ family, plans: list }));
}

/** `{ key, params }` for the period column (admin namespace). */
export function periodCopy(plan) {
  const days = plan?.period_days;
  if (days == null) return { key: 'plans.period.calendarMonth', params: {} };
  if (plan?.renews === false) return { key: 'plans.period.trial', params: { n: days } };
  if (days === 30) return { key: 'plans.period.monthly', params: {} };
  if (days === 365) return { key: 'plans.period.yearly', params: {} };
  return { key: 'plans.period.days', params: { n: days } };
}

/** The i18n key naming a grid cell's feature, or null (the caller then shows the cell's display_name). */
export function featureLabelKey(cell) {
  if (!cell) return null;
  if (cell.feature_id === PIPELINE_FEATURE_ID) return 'plans.features.pipeline';
  if (cell.kind === 'option' && typeof cell.value === 'string' && SLUG_RE.test(cell.value)) {
    return `plans.features.${cell.value}`;
  }
  return null;
}

/** The i18n key naming a family (grant key), or null (the caller then shows the raw key). */
export function familyLabelKey(family) {
  return typeof family === 'string' && SLUG_RE.test(family) ? `plans.families.${family}` : null;
}

/** Grid rows in the table's family order, then any key no listed family matches (e.g. `plan_ids: []`). */
export function gridRows(grid, familyOrder) {
  const rows = Array.isArray(grid?.grants) ? grid.grants : [];
  const rank = new Map((familyOrder ?? []).map((f, i) => [f, i]));
  const known = rows.filter((r) => rank.has(r.grant_key)).sort((a, b) => rank.get(a.grant_key) - rank.get(b.grant_key));
  const rest = rows.filter((r) => !rank.has(r.grant_key));
  return [...known, ...rest];
}
